import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { useBranding } from '@/lib/branding';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Shield, AlertCircle } from 'lucide-react';

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const { organisationName, logoUrl, loginLogoUrl, primaryColour } = useBranding();
  const navigate = useNavigate();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const user = await login(email, password);
      const isAdminUser = ['Super Admin', 'Admin', 'HR', 'Finance', 'Coordinator', 'Roster Manager'].includes(user.role_name);
      navigate(isAdminUser ? '/dashboard' : '/dashboard');
    } catch (err) {
      setError(err.response?.data?.detail || 'Login failed. Please check your credentials.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex">
      {/* Left - Branding */}
      <div className="hidden lg:flex lg:w-1/2 relative bg-gradient-to-br from-[hsl(var(--primary)/1)] to-[hsl(var(--primary)/0.75)] items-center justify-center p-12">
        <div className="relative z-10 text-white max-w-md">
          <div className="flex items-center gap-3 mb-8">
            {(loginLogoUrl || logoUrl) ? (
              <img src={loginLogoUrl || logoUrl} alt={organisationName} className="h-12 w-12 rounded-xl object-contain bg-white/20 p-1" data-testid="login-logo" />
            ) : (
              <div className="h-12 w-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Shield className="h-6 w-6 text-white" />
              </div>
            )}
            <h1 className="text-3xl font-bold tracking-tight" data-testid="login-org-name">{organisationName}</h1>
          </div>
          <h2 className="text-2xl font-semibold mb-4">NDIS Provider Management</h2>
          <p className="text-white/80 text-lg leading-relaxed">
            Manage participants, staff, funding, rosters, and timesheets — all in one place.
            Built for care providers who need clarity and control.
          </p>
          <div className="mt-12 grid grid-cols-2 gap-4">
            {['Roster Management', 'Funding Tracking', 'GPS Check-in', 'Invoice Export'].map((f) => (
              <div key={f} className="bg-white/10 rounded-lg px-4 py-3 text-sm text-white/90">{f}</div>
            ))}
          </div>
        </div>
        <div className="absolute inset-0 bg-[radial-gradient(800px_600px_at_30%_20%,rgba(255,255,255,0.08),transparent_60%)]" />
      </div>

      {/* Right - Login Form */}
      <div className="flex-1 flex items-center justify-center p-6 bg-background">
        <div className="w-full max-w-md flex flex-col gap-3">
        <Card className="w-full shadow-lg border-0 bg-card">
          <CardHeader className="space-y-2 text-center pb-2">
            <div className="lg:hidden flex items-center justify-center gap-2 mb-4">
              <div className="h-10 w-10 rounded-lg bg-primary flex items-center justify-center">
                <Shield className="h-5 w-5 text-primary-foreground" />
              </div>
              <h1 className="text-xl font-bold" data-testid="login-org-name-mobile">{organisationName}</h1>
            </div>
            <CardTitle className="text-2xl">Welcome back</CardTitle>
            <CardDescription>Sign in to your account to continue</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {error && (
                <Alert variant="destructive" data-testid="login-error-text">
                  <AlertCircle className="h-4 w-4" />
                  <AlertDescription>{error}</AlertDescription>
                </Alert>
              )}
              <div className="space-y-2">
                <Label htmlFor="email">Email address</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="admin@getcare360.local"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  data-testid="login-email-input"
                  className="h-11"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  data-testid="login-password-input"
                  className="h-11"
                />
              </div>
              <Button
                type="submit"
                className="w-full h-11"
                disabled={loading}
                data-testid="login-submit-button"
              >
                {loading ? 'Signing in...' : 'Sign in'}
              </Button>
              <a href="/forgot-password" className="block text-center text-sm text-muted-foreground hover:text-primary">Forgot password?</a>
            </form>
          </CardContent>
        </Card>
        {/* Stabilisation Blocker #5 (Feb 2026) — single-tenant deployment banner. */}
        <div
          className="flex items-start gap-2 rounded-md border border-slate-200 bg-white/70 px-3 py-2 text-[11px] text-slate-600 shadow-sm"
          data-testid="single-tenant-banner"
          role="note"
        >
          <span aria-hidden className="mt-0.5 inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-emerald-500" />
          <span className="leading-snug">
            <span className="font-semibold text-slate-700">Single-tenant deployment.</span>{' '}
            Data is scoped by branch and state. For multi-organisation rollout, see the V2 SaaS roadmap.
          </span>
        </div>
        </div>
      </div>
    </div>
  );
}
